import {
    ChannelInviteModalProps,
    ChannelInviteModalViewProps,
    ErrorTypes,
} from './types'
import ChannelInviteModalView from './ChannelInviteView'
import {NinchatEvents} from '../../../api/types'
import React from 'react'
import {channelInviteModal} from '../../../actions/modal'
import {describeAccess} from 'api/action/describeAccess'
import {joinChannel} from '../../../api/action/joinChannel'
import {useAppDispatch} from '../../../hooks'
import {useTranslation} from 'react-i18next'

type ModalProps = Omit<ChannelInviteModalViewProps, 'onAccept' | 'onDecline'>

const ChannelInviteModal = (props: ChannelInviteModalProps): React.ReactElement => {
    const {
        accessKey, noRedirectOnCancel,
    } = props
    const dispatch = useAppDispatch()
    const {t} = useTranslation()

    const defaultState: ModalProps = {
        channelAttrs: null,
        channelUnsilence: false,
        describeError: null,
        joinError: null,
        realmAttrs: null,
        realmMember: false,
        userAttrs: null,
        userId: '',
    }

    const [state, setState] = React.useState(defaultState)

    const closeModal = () => {
        dispatch(channelInviteModal({show: false}))
    }

    const onDecline = () => {
        if (!noRedirectOnCancel) {
            history.back()
        }
        closeModal()
    }

    const onDescribeAccessError = (error: NinchatEvents.NinchatErrorEvent): void => {
        switch (error.error_type) {
            case 'access_expired':
                setState({
                    ...state,
                    describeError: {
                        text: t('Channel invitations are valid for 14 days. You can try to request for a new invitation from your organization.'),
                        title: t('This invitation has been expired'),
                        type: ErrorTypes.expired,
                    },
                })
                break
            case 'access_denied':
                // access key is unknown
                // channel access: access key is user-specific but session user is no that user
                setState({
                    ...state,
                    describeError: {
                        text: t('This invitation is either invalid or for another user.'),
                        title: t('Invalid invitation'),
                    },
                })
                break
            default:
                setState({
                    ...state,
                    describeError: {
                        text: error.error_reason ? t('This invitation is invalid ({{reason}}).', {reason: error.error_reason}) : t('This invitation is invalid due to unknown reason.'),
                        title: t('Invalid invitation'),
                    },
                })
        }
    }

    const onJoinChannelError = (error: NinchatEvents.NinchatErrorEvent): void => {
        switch (error.error_type) {
            case 'channel_not_found':
                // channel with specified id does not exist
                setState({
                    ...state,
                    joinError: {
                        text: t('Channel cannot be joined because it does not exist.'),
                        title: t('Channel not found'),
                    },
                })
                break
            case 'permission_denied':
                // channel is closed and session user is not the owner and no access key or master signature was provided
                // channel is in realm, is not public, and session user is not realm member and no access key or master signature was provided
                // channel is private, session user is not the owner, and no access key or master signature providing full access was provided
                // channel requires verified identity and session user doesn't have one and no access key or master signature providing full access was provided
                // channel is suspended and session user is not the owner
                // user is in company but channel is not in that company and it is not invite-only and user has no access key
                // channel is in company but user is not
                setState({
                    ...state,
                    joinError: {
                        text: t('Channel cannot be joined because it is closed or due to insufficient permissions.'),
                        title: t('Permission denied'),
                    },
                })
                break
            default:
                setState({
                    ...state,
                    joinError: {
                        text: error.error_reason ? t('Channel cannot be joined ({{reason}}).', {reason: error.error_reason}) : t('Channel cannot be joined due to unknown reason.'),
                        title: t('Channel cannot be joined'),
                    },
                })
        }
    }

    const onAccept = () => {
        dispatch(joinChannel({access_key: accessKey}))
            .unwrap()
            .then(
                () => {
                    closeModal()
                }, onJoinChannelError,
            )
    }

    const onPopState = () => {
        // Close modal if user pressed "back" in browser
        closeModal()
    }

    React.useEffect(() => {
        window.addEventListener('popstate', onPopState)

        dispatch(describeAccess({access_key: accessKey}))
            .unwrap()
            .then(
                (event) => {
                    setState({
                        ...state,
                        ...event.user_attrs && {userAttrs: event.user_attrs},
                        ...event.channel_attrs && {channelAttrs: event.channel_attrs},
                        ...event.realm_attrs && {realmAttrs: event.realm_attrs},
                        channelUnsilence: event.channel_unsilence,
                        realmMember: event.realm_member,
                        userId: event.user_id,
                    })
                }, onDescribeAccessError,
            )

        return () => window.removeEventListener('popstate', onPopState)
    }, [])

    return (
        <ChannelInviteModalView {...state} onAccept={onAccept} onDecline={onDecline} />
    )
}
export default ChannelInviteModal
